import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { DynamicListsApi } from './api'
import type { AdoptPreview, CrmAccount, Preview, Rule } from './types'

const PREVIEW_DEBOUNCE_MS = 600

export const accountName = (a: CrmAccount) => a.profile_name || a.username || a.account_id

/** "12 min ago" style, from an ISO time. */
export function useAgo() {
  const { t } = useTranslation('dynamicLists')
  return (iso: string | null | undefined, now = Date.now()) => {
    if (!iso) return ''
    const min = Math.max(0, Math.floor((now - Date.parse(iso)) / 60000))
    if (min < 1) return t('ago.now')
    if (min < 60) return t('ago.minutes', { count: min })
    const h = Math.floor(min / 60)
    if (h < 48) return t('ago.hours', { count: h })
    return t('ago.days', { count: Math.floor(h / 24) })
  }
}

/** Ask `fetch` shortly after `key` stops changing; stale answers are dropped. */
function useDebounced<T>(fetch: (() => Promise<T>) | null, key: string) {
  const [state, setState] = useState<{ data: T | null; loading: boolean; error: string }>({ data: null, loading: false, error: '' })
  useEffect(() => {
    if (!fetch) {
      setState((s) => ({ ...s, loading: false }))
      return
    }
    let stale = false
    setState((s) => ({ ...s, loading: true }))
    const timer = window.setTimeout(() => {
      fetch().then(
        (data) => !stale && setState({ data, loading: false, error: '' }),
        (e: unknown) => !stale && setState((s) => ({ ...s, loading: false, error: e instanceof Error ? e.message : String(e) })),
      )
    }, PREVIEW_DEBOUNCE_MS)
    return () => {
      stale = true
      window.clearTimeout(timer)
    }
    // `key` stands for everything `fetch` closes over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, Boolean(fetch)])
  return state
}

/** Live counts for a rule on an Account. */
export function usePreview(api: DynamicListsApi, accountId: string, rule: Rule, ready: boolean, ownListId?: string | null) {
  const key = JSON.stringify([accountId, rule, ownListId ?? null])
  const state = useDebounced<Preview>(ready ? () => api.preview(accountId, rule, ownListId) : null, key)
  return { preview: state.data, loading: state.loading, error: state.error }
}

/** What the first update of an adopted list would add and remove. */
export function useAdoptPreview(api: DynamicListsApi, accountId: string, ofListId: string | null, rule: Rule, ready: boolean) {
  const key = JSON.stringify([accountId, ofListId, rule])
  return useDebounced<AdoptPreview>(ofListId && ready ? () => api.adoptPreview(accountId, ofListId, rule) : null, key)
}
